import React from "react";
import { motion } from "framer-motion";
import { resolveImg } from "@/lib/api";
import { BellRing } from "lucide-react";

function PlaceholderCover({ title }) {
  return (
    <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden book-shadow bg-gradient-to-br from-[#141414] via-[#0d0d0d] to-[#000] ring-1 ring-white/5">
      {/* subtle gold accent bar echoing brand covers */}
      <div className="absolute inset-y-0 right-0 w-[26%] bg-gradient-to-b from-gold/90 via-gold to-[#B5952F] opacity-90"
           style={{ clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0 100%)" }} />
      <div className="relative z-10 h-full flex flex-col justify-between p-5 text-left">
        <span className="text-[8px] tracking-[0.25em] text-gold/80 font-semibold uppercase">Kate Mercer</span>
        <div>
          <p className="font-display text-white text-lg leading-tight font-bold">{title}</p>
        </div>
        <span className="text-[9px] tracking-[0.3em] text-white/70 font-semibold uppercase">Kate Mercer</span>
      </div>
      {/* integrated diagonal gold ribbon */}
      <div className="absolute top-0 right-0 h-24 w-24 overflow-hidden pointer-events-none z-20">
        <div className="absolute rotate-45 bg-gradient-to-r from-[#E6C35A] to-[#B5952F] text-black text-[9px] font-bold tracking-[0.15em] text-center shadow-md"
             style={{ width: "160px", top: "26px", right: "-46px", paddingTop: "3px", paddingBottom: "3px" }}>
          COMING SOON
        </div>
      </div>
    </div>
  );
}

function ComingCard({ book, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="bg-white border border-gray-100 rounded-3xl p-7 text-center gold-glow"
      data-testid={`coming-soon-${book.slug || index}`}
    >
      <div className="mx-auto w-[150px] mb-6">
        {book.cover_image ? (
          <div className="relative">
            <img src={resolveImg(book.cover_image)} alt={book.title} className="w-full h-auto rounded-lg book-shadow" loading="lazy" />
            <div className="absolute top-0 right-0 h-24 w-24 overflow-hidden pointer-events-none">
              <div className="absolute rotate-45 bg-gradient-to-r from-[#E6C35A] to-[#B5952F] text-black text-[9px] font-bold tracking-[0.15em] text-center shadow-md"
                   style={{ width: "160px", top: "26px", right: "-46px", paddingTop: "3px", paddingBottom: "3px" }}>
                COMING SOON
              </div>
            </div>
          </div>
        ) : (
          <PlaceholderCover title={book.title} />
        )}
      </div>
      <h3 className="font-display text-2xl font-bold">{book.title}</h3>
      {book.description && <p className="text-sm text-gray-500 mt-3 leading-relaxed">{book.description}</p>}
      <a
        href="#newsletter"
        data-testid={`notify-me-${book.slug || index}`}
        className="btn-gold btn-pill btn-pill-sm mt-6"
      >
        <BellRing size={16} /> Notify Me
      </a>
    </motion.div>
  );
}

export default function ComingSoon({ books }) {
  const coming = books.filter((b) => b.coming_soon);
  const placeholders = [
    { slug: "book-four", title: "Book Four", description: "", cover_image: "" },
    { slug: "book-five", title: "Book Five", description: "", cover_image: "" },
  ];
  const cards = [...coming, ...placeholders].slice(0, 3);

  return (
    <section id="coming-soon" className="py-28 sm:py-36 bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">On the Horizon</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4">Coming Soon</h2>
          <p className="text-gray-600 mt-4">Be the first to know when Kate's next books arrive.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-7">
          {cards.map((b, i) => (
            <ComingCard key={b.slug || i} book={b} index={i} />
          ))}
        </div>
        <p className="text-center text-gray-500 mt-12 text-[15px]">More titles currently in development.</p>
      </div>
    </section>
  );
}
